import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Container, Row, Col, Card, Button, Spinner, Alert
} from "react-bootstrap";
import {
  FaFileDownload, FaArrowLeft, FaEye
} from "react-icons/fa";

const RegisteredAgentPreview = () => {
  const { documentId } = useParams();
  const navigate = useNavigate();
  const [document, setDocument] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchDocumentDetails();
  }, [documentId]);

  const fetchDocumentDetails = async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      setError("Authentication required");
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/auth/user`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (!response.ok) throw new Error("Failed to fetch document details");

      const data = await response.json();
      const documents = data.user.registeredAgentDocuments || [];
      const selectedDocument = documents.find(doc => doc._id === documentId || doc.id === documentId);

      if (!selectedDocument) {
        setError("Document not found");
      } else {
        setDocument(selectedDocument);
      }
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    if (document) {
      const fullUrl = `${process.env.REACT_APP_API_BASE_URL}${document.fileUrl}`;
      window.open(fullUrl, '_blank');
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <Spinner animation="border" variant="primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  if (error) {
    return (
      <Container className="py-5">
        <Alert variant="danger" className="text-center">
          {error}
          <Button
            variant="link"
            onClick={() => navigate('/registered-agent')}
          >
            Go Back
          </Button>
        </Alert>
      </Container>
    );
  }

  if (!document) {
    return (
      <Container className="py-5">
        <Alert variant="warning" className="text-center">
          Document not found
          <Button
            variant="link"
            onClick={() => navigate('/registered-agent')}
          >
            Go Back
          </Button>
        </Alert>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4">
      {/* Header */}
      <Row className="mb-4">
        <Col>
          <div className="d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center">
              <Button
                variant="outline-secondary"
                onClick={() => navigate('/registered-agent')}
                className="me-3"
              >
                <FaArrowLeft className="me-2" />
                Back to Documents
              </Button>
              <div>
                <h3 className="mb-1">Document Preview</h3>
                <p className="text-muted mb-0">
                  Preview and download your registered agent document
                </p>
              </div>
            </div>
            <Button
              variant="primary"
              onClick={handleDownload}
              className="d-flex align-items-center"
            >
              <FaFileDownload className="me-2" />
              Download
            </Button>
          </div>
        </Col>
      </Row>

      {/* Document Information */}
      <Card className="shadow-sm mb-4">
        <Card.Header>
          <h5 className="mb-0">
            <FaEye className="me-2" />
            Document Information
          </h5>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={6}>
              <div className="mb-3">
                <strong>Document Name:</strong>
                <p className="mb-1">{document.originalName}</p>
              </div>
              <div className="mb-3">
                <strong>File Size:</strong>
                <p className="mb-1">{formatFileSize(document.size || 0)}</p>
              </div>
            </Col>
            <Col md={6}>
              <div className="mb-3">
                <strong>Upload Date:</strong>
                <p className="mb-1">{new Date(document.date).toLocaleDateString()}</p>
              </div>
              <div className="mb-3">
                <strong>File Type:</strong>
                <p className="mb-1">
                  {document.fileUrl ? document.fileUrl.split('.').pop().toUpperCase() : 'Unknown'}
                </p>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Document Preview */}
      <Card className="shadow-sm">
        <Card.Header>
          <h5 className="mb-0">
            <FaEye className="me-2" />
            Document Preview
          </h5>
        </Card.Header>
        <Card.Body className="p-0">
          <iframe
            src={`${process.env.REACT_APP_API_BASE_URL}${document.fileUrl}`}
            width="100%"
            height="600px"
            title={`Preview of ${document.originalName}`}
            style={{ border: 'none' }}
          />
        </Card.Body>
      </Card>
    </Container>
  );
};

export default RegisteredAgentPreview;
